import * as actionTypes from "@/store/action-types";
import config from "@/api/config";
import environments from "@/shared/constants/environments";

export default {
  data() {
    return {
      paddleInitialized: false,
      subscriptionStatus: {
        interval: null,
        intervalCounter: 0,
        success: false,
        error: false,
      },
      pastDue: {
        loading: false,
        loaded: false,
        bankUrl: null,
        errorMsg: null,
      },
      mixin_user: {
        id: null,
        token: null
      },
    };
  },
  methods: {
    // Initialize Paddle SDK with vendor ID and environment
    planMixin_initializePaddle() {
      if (typeof window === "undefined" || !window.Paddle) {
        console.error("Paddle is not loaded");
        return false;
      }

      if (this.paddleInitialized) {
        return true;
      }

      const vendorId = config?.paddle?.vendorId;
      if (!vendorId) {
        console.error("Paddle vendor ID is missing from config");
        return false;
      }

      window.Paddle.Setup({ vendor: vendorId });
      if (
        window.Paddle.Environment &&
        [environments.LOCAL, environments.DEV].includes(config.environment)
      ) {
        window.Paddle.Environment.set("sandbox");
      }

      this.paddleInitialized = true;

      return true;
    },

    // Fetch billing update URL for past-due subscriptions
    async planMixin_loadPastDueUpdateUrl(
      subscriptionId = null,
      { force = false } = {}
    ) {
      const subId =
        subscriptionId ||
        this.user?.plan?.subscription?.id ||
        this.user?.plan?.subscription_id;

      if (!subId) {
        this.pastDue.loaded = false;
        this.pastDue.bankUrl = null;
        this.pastDue.errorMsg = "Subscription not found.";
        return null;
      }

      if (this.pastDue.loading) {
        return this.pastDue.bankUrl;
      }

      if (this.pastDue.loaded && this.pastDue.bankUrl && !force) {
        return this.pastDue.bankUrl;
      }

      this.pastDue.loading = true;
      this.pastDue.errorMsg = null;

      try {
        const response = await this.$store.dispatch(
          "subscription/getPastDueSubscription",
          { id: subId }
        );
        const updateUrl =
          response?.data?.data?.updateUrl ||
          response?.data?.data?.update_url ||
          null;
        this.pastDue.bankUrl = updateUrl;
        this.pastDue.loaded = true;
        return updateUrl;
      } catch (error) {
        this.pastDue.bankUrl = null;
        this.pastDue.loaded = false;
        this.pastDue.errorMsg =
          error?.body?.meta?.error_message ||
          error?.message ||
          "Unable to fetch billing update URL.";
        return null;
      } finally {
        this.pastDue.loading = false;
      }
    },

    // Subscribe to plan - opens Paddle checkout and polls for subscription
    planMixin_subscribePlan(planId, userData, callback = null, closeCallback = null) {
      this.planMixin_resetSubscriptionStatus();
      const metadata = { user_id: userData?.id };

      const checkoutConfig = {
        product: planId,
        theme: "none",
        email: userData.email,
        passthrough: JSON.stringify(metadata),
        marketingConsent: userData.subscribe_newsletter,
        successCallback: () => {
          // Continue polling after checkout succeeds
          this.planMixin_pollingSubscriptionStatus(callback);
        },
        closeCallback: () => {
          // User closed popup - reset state and call closeCallback if provided
          this.planMixin_resetSubscriptionStatus();
          if (closeCallback) closeCallback();
        },
        errorCallback: () => {
          // Actual error occurred - call callback with failure
          this.planMixin_resetSubscriptionStatus();
          if (callback) callback(false);
        },
      };

      if (window.Paddle) {
        this.$nextTick(() => window.Paddle.Checkout.open(checkoutConfig));
      } else {
        if (callback) callback(false);
      }
    },

    // Poll subscription status until found or limit reached
    planMixin_pollingSubscriptionStatus(callback = null) {
      this.subscriptionStatus.intervalCounter = 0;
      this.subscriptionStatus.success = false;
      this.subscriptionStatus.error = false;

      const pollingInterval = 4000;
      const intervalLimit = 15; // Increased to allow backend time to sync with Octopus

      // Simple delay before starting to give backend time to process webhook and sync with Octopus
      setTimeout(() => {
        // Start polling at regular intervals
        this.subscriptionStatus.interval = setInterval(async () => {
          this.subscriptionStatus.intervalCounter++;

          try {
            // Sync user data to check the subscription status
            const userSynced = await this.planMixin_syncLocalUser();

            // If user data is successfully synced and subscription exists
            if (userSynced) {
              clearInterval(this.subscriptionStatus.interval);
              this.subscriptionStatus.success = true;
              this.planMixin_handleSubscription(false);
              if (callback) callback(true);
              return;
            }
          } catch (err) {
            console.error("Polling error:", err);
          }

          // Check if polling limit is reached
          if (this.subscriptionStatus.intervalCounter >= intervalLimit) {
            clearInterval(this.subscriptionStatus.interval);
            this.subscriptionStatus.error = true;

            try {
              // Attempt a final sync
              await this.planMixin_syncLocalUser();
            } finally {
              this.planMixin_handleSubscription(true);
              if (callback) callback(false);
            }
          }
        }, pollingInterval);
      }, 3000); // 3 second delay before starting polling
    },

    // Sync user data - returns true if subscription found (exactly like old logic)
    async planMixin_syncLocalUser() {
      try {
        // Store token for authentication (like old logic)
        if (this.mixin_user.token) {
          localStorage.setItem('token', JSON.stringify(this.mixin_user.token))
        }

        const userId = this.mixin_user.id || this.user?.id
        const response = await this.$store.dispatch(actionTypes.FETCH_USER, {
          userId,
        });

        const user = response?.data?.data;

        // Remove token after fetching (like old logic)
        if (this.mixin_user.token) {
          localStorage.removeItem('token')
        }

        // Check if user has a subscription (including paused subscriptions)
        // We update user data regardless of subscription status to reflect changes
        const hasSubscription = !!user?.plan?.subscription
        // Always update user data if subscription exists (including paused/cancelled)
        // This ensures UI reflects the current subscription status
        if (hasSubscription) {
          await this.$store.dispatch(actionTypes.UPDATE_USER_LOCAL, user);
          localStorage.setItem("user", JSON.stringify(user));
          return true;
        }
        return false;
      } catch (error) {
        // Remove token on error (like old logic)
        if (this.mixin_user.token) {
          localStorage.removeItem('token')
        }
        return false;
      }
    },

    // Handle subscription status update
    planMixin_handleSubscription(isError) {
      if (!isError) {
        this.subscriptionStatus.success = true;
      } else {
        this.subscriptionStatus.error = true;
      }
    },

    // Reset subscription polling status
    planMixin_resetSubscriptionStatus() {
      if (this.subscriptionStatus.interval) {
        clearInterval(this.subscriptionStatus.interval);
      }

      this.subscriptionStatus = {
        interval: null,
        intervalCounter: 0,
        success: false,
        error: false,
      };
    },

    // Get plan features for user
    async planMixin_getPlanFeatures(userId) {
      try {
        const response = await this.$store.dispatch(actionTypes.FETCH_USER, {
          userId,
        });
        return response?.data?.data;
      } catch (error) {
        throw error;
      }
    },
  },
};
